// src/components/Login.js
import React from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from './Toast';
import { colors, radii, spacing, shadows } from '../theme/tokens';
import { Icon, BrandMark } from './ui';

const TIERS = [
  { icon: 'workspace_premium', name: 'Silver Tier', sub: 'Free', body: 'Search up to 5 car parts per day.' },
  { icon: 'military_tech', name: 'Gold Tier', sub: 'Premium', body: 'Unlimited searches plus add car parts.' },
  { icon: 'diamond', name: 'Platinum Tier', sub: 'Admin', body: 'Approve submissions and award tokens.' }
];

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();

  const handleSuccess = async (credentialResponse) => {
    try {
      const result = await login(credentialResponse);
      if (result.success) {
        showToast(`Welcome ${result.user.name}! Logged in as ${result.user.tier.toUpperCase()} tier.`, 'success');
        navigate('/');
      } else {
        showToast('Login failed: ' + result.error, 'error');
      }
    } catch (error) {
      console.error('Login error:', error);
      showToast('Login failed. Please try again.', 'error');
    }
  };

  const handleError = () => {
    console.error('Login Failed');
    showToast('Google login failed. Please try again.', 'error');
  };

  return (
    <div style={styles.container}>
      <div style={{ ...styles.orb, top: '-90px', left: '-70px' }} aria-hidden="true" />
      <div style={{ ...styles.orb, bottom: '-110px', right: '-80px', width: 320, height: 320 }} aria-hidden="true" />

      <div style={styles.card}>
        <div style={styles.brandRow}>
          <BrandMark size={64} />
        </div>
        <h1 style={styles.title}>Welcome back</h1>
        <p style={styles.subtitle}>Sign in to the PAR Car Parts Manager</p>

        <div style={styles.tierInfo}>
          {TIERS.map((t) => (
            <div key={t.name} style={styles.tier}>
              <span style={styles.tierIcon}>
                <Icon name={t.icon} size={22} color={colors.parGreenBright} />
              </span>
              <div>
                <div style={styles.tierName}>
                  {t.name} <span style={styles.tierSub}>{t.sub}</span>
                </div>
                <div style={styles.tierBody}>{t.body}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.loginButton}>
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={handleError}
            useOneTap
            theme="filled_black"
            size="large"
            shape="pill"
            text="continue_with"
          />
        </div>

        <p style={styles.note}>
          Sign in with Google to get started. New users start with Silver tier.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '20px',
    overflow: 'hidden'
  },
  orb: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(47,182,92,0.30) 0%, rgba(47,182,92,0) 70%)',
    filter: 'blur(16px)',
    pointerEvents: 'none'
  },
  card: {
    position: 'relative',
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.xl,
    padding: '40px',
    maxWidth: 480,
    width: '100%',
    boxShadow: shadows.lg,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    textAlign: 'center'
  },
  brandRow: { display: 'flex', justifyContent: 'center', marginBottom: 14 },
  title: { margin: '0 0 6px 0', color: colors.text, fontSize: 28, fontWeight: 800 },
  subtitle: { margin: '0 0 26px 0', color: colors.textMuted, fontSize: 15 },
  tierInfo: {
    textAlign: 'left',
    margin: '24px 0',
    padding: spacing.md,
    background: colors.surfaceSoft,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  tier: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  tierIcon: {
    width: 38, height: 38, flexShrink: 0, borderRadius: '50%',
    background: 'rgba(47,182,92,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  tierName: { fontSize: 14, fontWeight: 700, color: colors.text },
  tierSub: { fontSize: 12, fontWeight: 600, color: colors.parGreenBright, marginLeft: 4 },
  tierBody: { fontSize: 13, color: colors.textMuted },
  loginButton: { display: 'flex', justifyContent: 'center', margin: '26px 0 12px' },
  note: { fontSize: 12, color: colors.textFaint, margin: '14px 0 0 0' }
};

export default Login;
